<script setup>
// Auto-extracted chrome for Heizungssuche.vue — header + footer copied verbatim
// from Home so legal pages (Impressum/Datenschutz/AGB) render with the
// pilot's own masthead + footer (no AppLayout chrome leak).
// Body markup of the wrapper is yielded via <slot />.
import { computed } from 'vue';
import { Head } from '@inertiajs/vue3';

const props = defineProps({
    domain: { type: Object, default: () => ({}) },
    brandName: { type: String, default: "Heizungssuche" },
    brandTagline: { type: String, default: "Bundesweit. Nach Technologie. Geprüfter Pfad." },
    brandAccent: { type: String, default: '#0EA5E9' },
    author: { type: Object, default: null },
    designVersion: { type: String, default: '' },
});

// Derive a few values that the chrome HTML references. Best-effort fallbacks
// for the values the chrome's masthead/footer reads.
const today = computed(() => new Date().toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }));
const brandName = computed(() => props.brandName || props.domain?.settings?.brand_name || "Heizungssuche");
const brandTagline = computed(() => props.brandTagline || props.domain?.settings?.brand_tagline || "Bundesweit. Nach Technologie. Geprüfter Pfad.");
const brandAccent = computed(() => props.brandAccent);
const author = computed(() => props.author);
const DESIGN_VERSION = computed(() => props.designVersion || '');


const fontsHref = "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@500;600&display=swap";
</script>

<template>
    <div class="hsu" :style="`--hsu-accent:${brandAccent};`">
        <Head>
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
            <link rel="stylesheet" :href="fontsHref" />
        </Head>
        <header class="hsu-rail">
                    <div class="hsu-rail__inner">
                        <a class="hsu-mark" href="/" aria-label="Startseite Heizungssuche">
                            <span class="hsu-mark__sigil" aria-hidden="true">
                                <svg viewBox="0 0 24 24" width="22" height="22"><path d="M5 13l3-7h8l3 7v6H5z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M9 13c0-2 3-2 3-4 0 2 3 2 3 4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
                            </span>
                            <span class="hsu-mark__t">
                                <strong>{{ brandName }}</strong>
                                <span>{{ brandTagline }}</span>
                            </span>
                        </a>
                        <nav class="hsu-rail__nav" aria-label="Sekundärnavigation">
                            <a href="#hsu-tech">Technologien</a>
                            <a href="#hsu-filter">Verzeichnis</a>
                            <a href="#hsu-faq">Fragen</a>
                            <a href="#hsu-form" class="hsu-rail__cta">ANFRAGE</a>
                            <a href="/ratgeber">Ratgeber</a>
                        </nav>
                    </div>
                </header>
        <slot />
        <footer class="hsu-foot">
                    <div class="hsu-foot__inner">
                        <div class="hsu-foot__brand">
                            <span class="hsu-mark__sigil" aria-hidden="true">
                                <svg viewBox="0 0 24 24" width="18" height="18"><path d="M5 13l3-7h8l3 7v6H5z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>
                            </span>
                            <span>{{ brandName }} &middot; {{ brandTagline }}</span>
                        </div>
                        <nav class="hsu-foot__links" aria-label="Footer">
                            <a href="/impressum">Impressum</a>
                            <a href="/datenschutz">Datenschutz</a>
                            <a href="/agb">AGB</a>
                            <a href="/widerruf">Widerruf</a>
                            <a href="/ratgeber">Ratgeber</a>
                        </nav>
                        <p class="hsu-foot__copy">
                            &copy; {{ new Date().getFullYear() }} heizungssuche.de &middot; Auswahl pending HWK-Sync, alle Angaben ohne Gewähr.
                        </p>
                    </div>
                </footer>
    </div>
</template>

<style scoped>

/* hsu-* namespace — Heizungssuche v3.1 gridrail. Unique structure vs hbb-* / ea-* / sht-*.
   Reset is local-scoped to .hsu to avoid bleed. */

.hsu {
    --hsu-bg: #fafaf7;
    --hsu-ink: #0b1411;
    --hsu-muted: #5a6661;
    --hsu-line: #d8dcd5;
    --hsu-lineHi: #b7c0b8;
    --hsu-surface: #ffffff;
    --hsu-surfaceAlt: #f1f3ee;
    --hsu-accent: #16A34A;
    --hsu-accentInk: #0c5a2a;
    --hsu-accentSoft: #e6f6ec;
    --hsu-rad: 4px;
    --hsu-radLg: 10px;
    font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
    background: var(--hsu-bg);
    color: var(--hsu-ink);
    line-height: 1.55;
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
}
.hsu *, .hsu *::before, .hsu *::after { box-sizing: border-box; }
.hsu a { color: inherit; text-decoration: none; }
.hsu button { font: inherit; color: inherit; background: none; border: 0; cursor: pointer; }
.hsu h1, .hsu h2, .hsu h3 { font-family: 'Space Grotesk', 'Inter', sans-serif; font-weight: 700; letter-spacing: -0.01em; margin: 0; }
.hsu p { margin: 0; }
.hsu-mono { font-family: 'JetBrains Mono', ui-monospace, Menlo, Consolas, monospace; font-feature-settings: 'tnum' 1, 'zero' 1; letter-spacing: 0; }

/* ── Top rail ─────────────────────────────────────────── */
.hsu-rail { border-bottom: 1px solid var(--hsu-line); background: var(--hsu-surface); position: sticky; top: 0; z-index: 30; }
.hsu-rail__inner { max-width: 1240px; margin: 0 auto; padding: 14px 20px; display: flex; align-items: center; justify-content: space-between; gap: 24px; }
.hsu-mark { display: inline-flex; align-items: center; gap: 12px; }
.hsu-mark__sigil { display: inline-grid; place-items: center; width: 36px; height: 36px; border-radius: 8px; background: var(--hsu-accentSoft); color: var(--hsu-accentInk); }
.hsu-mark__t { display: flex; flex-direction: column; line-height: 1.15; }
.hsu-mark__t strong { font-family: 'Space Grotesk', sans-serif; font-size: 16px; }
.hsu-mark__t span { font-size: 11px; color: var(--hsu-muted); letter-spacing: 0.02em; }
.hsu-rail__nav { display: none; gap: 22px; font-size: 14px; align-items: center; }
.hsu-rail__nav a { color: var(--hsu-muted); }
.hsu-rail__nav a:hover { color: var(--hsu-ink); }
.hsu-rail__cta { padding: 8px 14px; border-radius: 999px; background: var(--hsu-ink); color: #fff !important; font-weight: 600; font-size: 12px; letter-spacing: 0.08em; }
@media (min-width: 880px) { .hsu-rail__nav { display: inline-flex; } }

/* ── Buttons ─────────────────────────────────────────── */
.hsu-btn { display: inline-flex; align-items: center; gap: 10px; padding: 12px 18px; border-radius: var(--hsu-rad); font-weight: 600; transition: transform .08s ease, background .15s ease, color .15s ease; }
.hsu-btn:active { transform: translateY(1px); }
.hsu-btn:disabled { opacity: .5; cursor: not-allowed; transform: none; }
.hsu-btn--solid { background: var(--hsu-accent); color: #fff; text-transform: uppercase; letter-spacing: 0.06em; box-shadow: inset 0 -2px 0 rgba(0,0,0,.15); }
.hsu-btn--solid:hover:not(:disabled) { background: var(--hsu-accentInk); }
.hsu-btn--md { font-size: 13px; padding: 11px 16px; }
.hsu-btn--xl { font-size: 15px; padding: 16px 24px; }
.hsu-btn--full { width: 100%; justify-content: center; }
.hsu-btn__arr { display: inline-grid; place-items: center; }

/* ── Hero ────────────────────────────────────────────── */
.hsu-hero { border-bottom: 1px solid var(--hsu-line); background: var(--hsu-surface); }
.hsu-hero__grid { max-width: 1240px; margin: 0 auto; padding: 44px 20px 28px; display: grid; gap: 28px; grid-template-columns: 1fr; }
@media (min-width: 880px) {
    .hsu-hero__grid { grid-template-columns: 180px 1fr; gap: 48px; padding: 64px 32px 36px; align-items: start; }
}
.hsu-hero__meta { display: flex; flex-direction: row; gap: 16px; padding-top: 4px; }
@media (min-width: 880px) {
    .hsu-hero__meta { flex-direction: column; border-right: 1px solid var(--hsu-line); padding-right: 24px; }
}
.hsu-hero__metaItem { display: flex; flex-direction: column; gap: 2px; }
.hsu-hero__metaItem span:first-child { font-size: 28px; font-weight: 700; line-height: 1; color: var(--hsu-ink); }
.hsu-hero__metaItem span:last-child { font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em; color: var(--hsu-muted); }
.hsu-hero__eyebrow { font-size: 12px; text-transform: uppercase; letter-spacing: 0.16em; color: var(--hsu-accentInk); margin-bottom: 14px; font-weight: 600; }
.hsu-hero__h { font-size: clamp(34px, 5.8vw, 60px); line-height: 1.04; max-width: 22ch; }
.hsu-hero__hEm { display: block; color: var(--hsu-muted); font-weight: 600; font-style: normal; }
.hsu-hero__sub { margin-top: 18px; max-width: 56ch; font-size: 17px; color: #2a3431; }
.hsu-hero__ctaRow { margin-top: 26px; display: flex; flex-direction: column; gap: 12px; align-items: flex-start; }
.hsu-hero__byline { font-size: 12px; color: var(--hsu-muted); }
.hsu-hero__byline a { text-decoration: underline; text-decoration-color: var(--hsu-lineHi); text-underline-offset: 3px; }

/* ── Tech grid ────────────────────────────────────────── */
.hsu-techGrid { max-width: 1240px; margin: 0 auto; padding: 0 20px 48px; display: grid; gap: 1px; grid-template-columns: repeat(2, 1fr); background: var(--hsu-line); border: 1px solid var(--hsu-line); border-radius: var(--hsu-radLg); overflow: hidden; }
@media (min-width: 720px) { .hsu-techGrid { grid-template-columns: repeat(5, 1fr); margin: 0 auto; max-width: 1176px; padding: 0; margin-bottom: 64px; } }
.hsu-tile { display: flex; flex-direction: column; align-items: flex-start; gap: 8px; padding: 22px 18px 20px; background: var(--hsu-surface); text-align: left; min-height: 138px; position: relative; transition: background .15s ease; }
.hsu-tile:hover { background: var(--hsu-surfaceAlt); }
.hsu-tile__idx { font-size: 11px; color: var(--hsu-muted); }
.hsu-tile__name { font-family: 'Space Grotesk', sans-serif; font-size: 18px; font-weight: 700; }
.hsu-tile__count { font-size: 12px; color: var(--hsu-muted); margin-top: auto; }
.hsu-tile__mark { position: absolute; top: 16px; right: 16px; width: 22px; height: 22px; border: 1px solid var(--hsu-lineHi); border-radius: 50%; display: grid; place-items: center; color: transparent; transition: all .15s ease; }
.hsu-tile--on { background: var(--hsu-accentSoft); }
.hsu-tile--on .hsu-tile__mark { background: var(--hsu-accent); border-color: var(--hsu-accent); color: #fff; }
.hsu-tile--on .hsu-tile__idx { color: var(--hsu-accentInk); }

/* ── Board (filter + cards) ──────────────────────────── */
.hsu-board { background: var(--hsu-bg); padding: 56px 0 64px; }
.hsu-board__inner { max-width: 1240px; margin: 0 auto; padding: 0 20px; }
.hsu-board__kicker { font-size: 12px; color: var(--hsu-muted); text-transform: uppercase; letter-spacing: 0.12em; }
.hsu-board__h { font-size: clamp(26px, 3.5vw, 36px); margin-top: 6px; }
.hsu-board__lead { margin-top: 10px; max-width: 60ch; color: #2a3431; }
.hsu-controls { margin-top: 28px; display: grid; gap: 18px; padding: 18px; background: var(--hsu-surface); border: 1px solid var(--hsu-line); border-radius: var(--hsu-radLg); }
@media (min-width: 880px) { .hsu-controls { grid-template-columns: 200px 1fr; gap: 24px; align-items: start; } .hsu-controls > :last-child { grid-column: 1 / -1; } }
.hsu-ctl__lbl { display: block; font-size: 11px; letter-spacing: 0.12em; color: var(--hsu-muted); margin-bottom: 8px; }
.hsu-ctl--plz .hsu-ctl__in { width: 160px; padding: 12px 14px; border: 1px solid var(--hsu-lineHi); border-radius: var(--hsu-rad); font-size: 18px; }
.hsu-ctl--plz .hsu-ctl__in:focus { outline: 2px solid var(--hsu-accent); outline-offset: 1px; border-color: var(--hsu-accent); }
.hsu-ctl__err { color: #b91c1c; font-size: 12px; margin-top: 6px; }
.hsu-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.hsu-chip { padding: 8px 14px; border: 1px solid var(--hsu-lineHi); border-radius: 999px; font-size: 13px; color: var(--hsu-muted); transition: all .12s ease; }
.hsu-chip:hover { color: var(--hsu-ink); border-color: var(--hsu-ink); }
.hsu-chip--on { background: var(--hsu-ink); color: #fff !important; border-color: var(--hsu-ink); }

.hsu-board__summary { margin-top: 18px; display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; padding-bottom: 6px; border-bottom: 1px solid var(--hsu-line); }
.hsu-board__count { font-size: 14px; color: var(--hsu-muted); }
.hsu-board__count span.hsu-mono { color: var(--hsu-ink); font-weight: 700; font-size: 18px; margin-right: 4px; }

.hsu-empty { margin-top: 24px; padding: 24px; border: 1px dashed var(--hsu-lineHi); border-radius: var(--hsu-rad); color: var(--hsu-muted); text-align: center; }
.hsu-placeholder { margin-top: 24px; padding: 26px 24px; border: 1px solid var(--hsu-lineHi); background: var(--hsu-surface); border-radius: var(--hsu-rad); }
.hsu-placeholder__lead { margin: 0 0 14px; font-size: 15px; color: var(--hsu-ink); }
.hsu-placeholder__lead strong { font-weight: 700; }
.hsu-placeholder__bullets { margin: 0 0 16px; padding-left: 22px; color: var(--hsu-ink); font-size: 14px; }
.hsu-placeholder__bullets li { margin: 6px 0; }
.hsu-placeholder__cta { margin: 0; }
.hsu-placeholder__link { color: #16A34A; font-weight: 700; text-decoration: underline; }

.hsu-cards { margin-top: 24px; display: grid; gap: 14px; grid-template-columns: 1fr; }
@media (min-width: 720px) { .hsu-cards { grid-template-columns: repeat(2, 1fr); } }
@media (min-width: 1080px) { .hsu-cards { grid-template-columns: repeat(3, 1fr); } }
.hsu-card { background: var(--hsu-surface); border: 1px solid var(--hsu-line); border-radius: var(--hsu-radLg); padding: 18px; display: flex; flex-direction: column; gap: 10px; position: relative; }
.hsu-card--pick { border-color: var(--hsu-accent); box-shadow: 0 0 0 1px var(--hsu-accent) inset; }
.hsu-card__top { display: flex; align-items: center; gap: 10px; font-size: 11px; color: var(--hsu-muted); text-transform: uppercase; letter-spacing: 0.08em; }
.hsu-card__no { font-size: 13px; color: var(--hsu-ink); font-weight: 700; }
.hsu-card__pin { padding: 2px 8px; background: var(--hsu-accent); color: #fff; border-radius: 999px; font-weight: 600; }
.hsu-card__reg { margin-left: auto; }
.hsu-card__name { font-size: 18px; }
.hsu-card__addr { display: flex; gap: 8px; align-items: baseline; color: var(--hsu-muted); font-size: 14px; }
.hsu-card__addr .hsu-mono { color: var(--hsu-ink); font-weight: 600; }
.hsu-card__profil { font-size: 14px; color: #2a3431; }
.hsu-card__tech { list-style: none; padding: 0; margin: 0; display: flex; flex-wrap: wrap; gap: 6px; }
.hsu-card__tech li { font-size: 11px; padding: 4px 8px; border: 1px solid var(--hsu-line); border-radius: 4px; color: var(--hsu-muted); }
.hsu-card__tech li.hsu-card__tech--hi { background: var(--hsu-accentSoft); color: var(--hsu-accentInk); border-color: var(--hsu-accent); font-weight: 600; }
.hsu-card__facts { margin: 4px 0 0; display: flex; gap: 16px; border-top: 1px dashed var(--hsu-line); padding-top: 10px; }
.hsu-card__facts > div { display: flex; flex-direction: column; gap: 2px; }
.hsu-card__facts dt { font-size: 10px; text-transform: uppercase; letter-spacing: 0.1em; color: var(--hsu-muted); }
.hsu-card__facts dd { margin: 0; font-size: 13px; }
.hsu-card__region { margin: 0; font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em; color: var(--hsu-muted); font-weight: 600; }
.hsu-card__owner { margin: 0; font-size: 13px; color: var(--hsu-muted); }
.hsu-card__tags { list-style: none; padding: 0; margin: 0; display: flex; flex-wrap: wrap; gap: 6px; }
.hsu-card__tags li { font-size: 11px; padding: 3px 8px; border: 1px solid var(--hsu-line); border-radius: 999px; color: var(--hsu-muted); background: #fff; }
.hsu-card__contact { display: flex; flex-direction: column; gap: 4px; font-size: 13px; }
.hsu-card__contact a { color: #16A34A; text-decoration: none; font-weight: 600; }
.hsu-card__contact a:hover { text-decoration: underline; }
.hsu-card__hwk { margin: 4px 0 0; font-size: 11px; color: var(--hsu-muted); }

/* ── Lead form ────────────────────────────────────────── */
.hsu-form { background: var(--hsu-surface); border-top: 1px solid var(--hsu-line); padding: 64px 0; }
.hsu-form__inner { max-width: 880px; margin: 0 auto; padding: 0 20px; }
.hsu-form__kicker { font-size: 12px; color: var(--hsu-muted); text-transform: uppercase; letter-spacing: 0.12em; }
.hsu-form__h { font-size: clamp(26px, 3.4vw, 36px); margin-top: 6px; }
.hsu-form__lead { margin-top: 10px; color: #2a3431; max-width: 56ch; }
.hsu-form__pre { margin: 24px 0 8px; }
.hsu-form__warn { padding: 14px 16px; background: #fff8e1; border: 1px solid #f1d28a; border-radius: var(--hsu-rad); color: #6b4f00; font-size: 14px; }
.hsu-form__ack { padding: 14px 16px; background: var(--hsu-accentSoft); border: 1px solid var(--hsu-accent); border-radius: var(--hsu-rad); font-size: 14px; }
.hsu-form__ackLine { font-size: 11px; letter-spacing: 0.14em; color: var(--hsu-accentInk); margin-bottom: 4px; }

.hsu-fset { margin-top: 16px; display: flex; flex-direction: column; gap: 20px; }
.hsu-block { border: 1px solid var(--hsu-line); border-radius: var(--hsu-radLg); padding: 20px 18px 18px; background: var(--hsu-surface); }
.hsu-block__legend { padding: 0 8px; margin-left: -4px; font-size: 13px; font-weight: 700; display: inline-flex; align-items: center; gap: 8px; }
.hsu-block__legend .hsu-mono { display: inline-grid; place-items: center; width: 22px; height: 22px; background: var(--hsu-ink); color: #fff; border-radius: 4px; font-size: 11px; font-weight: 700; }
.hsu-block__grid { display: grid; gap: 14px; grid-template-columns: 1fr; margin-top: 6px; }
@media (min-width: 640px) { .hsu-block__grid { grid-template-columns: 1fr 1fr; } .hsu-fld--wide { grid-column: 1 / -1; } }

.hsu-fld { display: flex; flex-direction: column; gap: 6px; }
.hsu-fld__lbl { font-size: 13px; font-weight: 600; color: var(--hsu-ink); }
.hsu-fld__in, .hsu-fld__sel { padding: 11px 12px; border: 1px solid var(--hsu-lineHi); border-radius: var(--hsu-rad); background: var(--hsu-surface); font: inherit; color: var(--hsu-ink); }
.hsu-fld__in:focus, .hsu-fld__sel:focus { outline: 2px solid var(--hsu-accent); outline-offset: 1px; border-color: var(--hsu-accent); }
.hsu-fld__rng { width: 100%; }
.hsu-fld__rngTicks { display: flex; justify-content: space-between; font-size: 11px; color: var(--hsu-muted); font-family: 'JetBrains Mono', monospace; }
.hsu-fld--err .hsu-fld__in { border-color: #b91c1c; }
.hsu-fld__err { color: #b91c1c; font-size: 12px; }
.hsu-fld__err--center { text-align: center; }

.hsu-consent { margin-top: 14px; display: flex; gap: 12px; align-items: flex-start; padding: 14px; border: 1px solid var(--hsu-line); border-radius: var(--hsu-rad); background: var(--hsu-surfaceAlt); }
.hsu-consent input { margin-top: 4px; width: 18px; height: 18px; accent-color: var(--hsu-accent); }
.hsu-consent__t { font-size: 12px; line-height: 1.55; color: #2a3431; }
.hsu-consent--err { border-color: #b91c1c; }

.hsu-submit { margin-top: 12px; display: flex; flex-direction: column; gap: 8px; align-items: stretch; }
.hsu-submit__meta { text-align: center; font-size: 12px; color: var(--hsu-muted); }

.hsu-done { padding: 36px 24px; text-align: center; border: 1px solid var(--hsu-accent); background: var(--hsu-accentSoft); border-radius: var(--hsu-radLg); color: var(--hsu-accentInk); display: flex; flex-direction: column; align-items: center; gap: 8px; }
.hsu-done h3 { font-size: 22px; }
.hsu-done__meta { font-size: 13px; color: var(--hsu-accentInk); }
.hsu-done__meta code { background: rgba(255,255,255,.6); padding: 2px 6px; border-radius: 4px; }

/* ── FAQ ──────────────────────────────────────────────── */
.hsu-faq { background: var(--hsu-bg); padding: 64px 0 80px; border-top: 1px solid var(--hsu-line); }
.hsu-faq__inner { max-width: 880px; margin: 0 auto; padding: 0 20px; }
.hsu-faq__kicker { font-size: 12px; color: var(--hsu-muted); text-transform: uppercase; letter-spacing: 0.12em; }
.hsu-faq__h { font-size: clamp(26px, 3.4vw, 36px); margin-top: 6px; }
.hsu-faq__list { margin: 28px 0 0; padding: 0; display: flex; flex-direction: column; gap: 14px; }
.hsu-faq__row { background: var(--hsu-surface); border: 1px solid var(--hsu-line); border-radius: var(--hsu-radLg); padding: 18px 20px; }
.hsu-faq__row dt { display: flex; gap: 10px; font-weight: 700; font-family: 'Space Grotesk', sans-serif; font-size: 16px; }
.hsu-faq__row dt .hsu-mono { color: var(--hsu-muted); font-weight: 600; }
.hsu-faq__row dd { margin: 6px 0 0; color: #2a3431; font-size: 14.5px; }

/* ── Footer ───────────────────────────────────────────── */
.hsu-foot { background: var(--hsu-ink); color: #cdd5d0; padding: 36px 0 28px; }
.hsu-foot__inner { max-width: 1240px; margin: 0 auto; padding: 0 20px; display: flex; flex-direction: column; gap: 16px; }
.hsu-foot__brand { display: inline-flex; align-items: center; gap: 10px; color: #fff; font-family: 'Space Grotesk', sans-serif; }
.hsu-foot__brand .hsu-mark__sigil { background: rgba(255,255,255,.08); color: var(--hsu-accent); }
.hsu-foot__links { display: flex; flex-wrap: wrap; gap: 18px; font-size: 13px; }
.hsu-foot__links a { color: #cdd5d0; }
.hsu-foot__links a:hover { color: #fff; }
.hsu-foot__copy { font-size: 12px; color: #8a948f; }

/* ── Sticky CTA ───────────────────────────────────────── */
.hsu-stick { position: fixed; left: 0; right: 0; bottom: 0; background: var(--hsu-ink); color: #fff; border-top: 1px solid #000; z-index: 40; }
.hsu-stick__inner { max-width: 1240px; margin: 0 auto; padding: 12px 16px; display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.hsu-stick__t { display: flex; flex-direction: column; line-height: 1.2; font-size: 12px; }
.hsu-stick__t strong { font-size: 13px; }
.hsu-stick__t span { color: #b7c0b8; }
@media (min-width: 880px) { .hsu-stick { display: none; } }

/* ── Trust strip (images + cites + byline) — hsu-* unique treatment ── */
.hsu-trustbar {
    background: #f7f3ea;
    padding: 32px 18px;
    border-top: 1px solid rgba(20,17,13,0.08);
    border-bottom: 1px solid rgba(20,17,13,0.08);
}
.hsu-trustbar__inner {
    max-width: 1120px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr;
    gap: 22px;
    align-items: start;
}
@media (min-width: 820px) {
    .hsu-trustbar__inner {
        grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
        gap: 36px;
    }
}
.hsu-trustbar__photo {
    margin: 0;
    width: 100%;
    aspect-ratio: 4 / 3;
    border-radius: 14px;
    overflow: hidden;
    position: relative;
    background: #fff;
    border: 1px solid rgba(20,17,13,0.10); border-top: 6px solid #16A34A; box-shadow: 0 6px 22px -14px rgba(20,17,13,0.20);
}
.hsu-trustbar__photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}
.hsu-trustbar__chip {
    position: absolute;
    top: 12px;
    left: 12px;
    background: rgba(255,255,255,0.94);
    color: #14110d;
    font-size: 11px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    font-weight: 600;
    padding: 6px 10px;
    border-radius: 999px;
    border: 1px solid rgba(20,17,13,0.08);
    backdrop-filter: blur(6px);
    margin: 0;
}
.hsu-trustbar__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.hsu-trustbar__lead {
    margin: 0;
    color: #3f3a32;
    font-size: 15px;
    line-height: 1.5;
    font-family: 'Inter', system-ui, sans-serif;
}
.hsu-trustbar__pills {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.hsu-trustbar__pill {
    display: inline-flex;
    align-items: center;
    border: 1px solid #16A34A55;
    color: #14110d;
    background: #fff;
    border-radius: 999px;
    padding: 6px 12px;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.04em;
}
.hsu-trustbar__byline {
    display: flex;
    align-items: center;
    gap: 12px;
}
.hsu-trustbar__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #16A34A;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: 13px;
    letter-spacing: 0.02em;
    flex-shrink: 0;
}
.hsu-trustbar__bytxt {
    display: flex;
    flex-direction: column;
    line-height: 1.25;
}
.hsu-trustbar__byname {
    font-size: 13px;
    font-weight: 600;
    color: #14110d;
}
.hsu-trustbar__byrole {
    font-size: 12px;
    color: #3f3a32;
}
.hsu-trustbar__feature {
    margin: 8px 0 0;
    max-width: 360px;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid rgba(20,17,13,0.10);
    box-shadow: 0 6px 22px -16px rgba(20,17,13,0.20);
    background: #fff;
}
.hsu-trustbar__feature img {
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 10;
    object-fit: cover;
    display: block;
}
.hsu-trustbar__feature figcaption {
    font-size: 11px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #3f3a32;
    padding: 8px 12px;
    background: #fff;
}


</style>
